import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { usePerformanceAggregation } from '../../../hooks/usePmoBoard.js';
import { ErrorState } from '../../feedback/ErrorState/index.js';
import { downloadCsv, fileTimestamp, toCsv } from '../../../lib/csv.js';
import {
  buildAreaCsvRows,
  formatPoint,
  getAreaAccent,
  isDivisionArea,
  printAreaReport,
  reportFileBase,
  resolveAreas,
  type CsvVariant,
  type ReportMeta,
  type ResolvedArea,
} from '../../../lib/performance-agg-ui.js';
import type { PerformanceAggMode } from '../../../types/project.js';
import { PerformanceReportModal } from './PerformanceReportModal.js';

/** 集計対象として選択されたエリア + メタ情報（プレビューモーダル用）。 */
interface PreviewTarget {
  area: ResolvedArea;
  meta: ReportMeta;
}

/** YYYY-MM-DD へゼロ埋め整形する。 */
/** @graph-connects none */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板「実績集計」ビュー。期間レンジを手動指定し、開発/designブロックごとに完了pt実績を母票の事業単位で集計。開発ブロックには担当Div(Responsible Div)軸の「PI Div」カードも並ぶ。各エリアをプレビュー(大モーダル)/CSV/PDF出力できる
 * @graph-connects PerformanceAggBlock [calls] 開発/designブロック描画
 * @graph-connects PerformanceReportModal [calls] レポートプレビューモーダル描画
 */
export function PerformanceAggView() {
  const { t } = useTranslation();

  // 既定期間: 当月1日 〜 今日
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${String(now.getFullYear())}-${pad2(now.getMonth() + 1)}-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return `${String(now.getFullYear())}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  });

  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  const datesValid = startDate !== '' && endDate !== '' && startDate <= endDate;

  return (
    <div className="space-y-6">
      {/* 期間レンジ指定（開発・design 両ブロック共通） */}
      <Card className="animate-content-reveal">
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                {t('pmoBoard.performanceAgg.startDate')}
              </label>
              <Input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => {
                  setStartDate(e.target.value);
                }}
                size="md"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                {t('pmoBoard.performanceAgg.endDate')}
              </label>
              <Input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => {
                  setEndDate(e.target.value);
                }}
                size="md"
              />
            </div>
            <p className="text-xs text-[var(--color-text-muted)] sm:pb-2">
              {t('pmoBoard.performanceAgg.periodHint')}
            </p>
          </div>
          {!datesValid && (
            <p className="mt-2 text-xs text-[var(--color-error)]">
              {t('pmoBoard.performanceAgg.invalidRange')}
            </p>
          )}
        </CardContent>
      </Card>

      <PerformanceAggBlock
        mode="dev"
        startDate={startDate}
        endDate={endDate}
        datesValid={datesValid}
        onPreview={setPreview}
      />
      <PerformanceAggBlock
        mode="design"
        startDate={startDate}
        endDate={endDate}
        datesValid={datesValid}
        onPreview={setPreview}
      />

      <PerformanceReportModal
        open={preview !== null}
        onClose={() => {
          setPreview(null);
        }}
        area={preview?.area ?? null}
        meta={preview?.meta ?? null}
      />
    </div>
  );
}

interface PerformanceAggBlockProps {
  mode: PerformanceAggMode;
  startDate: string;
  endDate: string;
  datesValid: boolean;
  onPreview: (target: PreviewTarget) => void;
}

/**
 * モード別ブロック（開発 / design）。集計実行 → エリアカード描画。
 * 事業軸のカードと担当Div 軸のカード（PI Div）を別ブロックに分けて並べる。
 * @graph-connects usePerformanceAggregation [calls] 実績集計クエリ実行
 * @graph-connects AreaCard [calls] 集計エリアカード描画
 */
function PerformanceAggBlock({
  mode,
  startDate,
  endDate,
  datesValid,
  onPreview,
}: PerformanceAggBlockProps) {
  const { t } = useTranslation();
  const [run, setRun] = useState(false);

  // 期間が変わったら集計結果をリセット（古い結果が残らないように）。
  useEffect(() => {
    setRun(false);
  }, [startDate, endDate]);

  const query = usePerformanceAggregation(mode, startDate, endDate, run);
  const areas = useMemo(
    () => (query.data ? resolveAreas(mode, query.data) : []),
    [mode, query.data],
  );
  // 事業軸（business）と担当Div 軸（division）は別ブロックに分けて描く。
  const businessAreas = useMemo(
    () => areas.filter((area) => !isDivisionArea(mode, area.key)),
    [areas, mode],
  );
  const divisionAreas = useMemo(
    () => areas.filter((area) => isDivisionArea(mode, area.key)),
    [areas, mode],
  );

  const modeLabel = t(`pmoBoard.performanceAgg.mode.${mode}`);

  const areaLabel = (key: string): string =>
    key === 'all'
      ? t(`pmoBoard.performanceAgg.areas.all.${mode}`)
      : t(`pmoBoard.performanceAgg.areas.${key}`);

  const makeMeta = (key: string): ReportMeta => ({
    modeLabel,
    areaLabel: areaLabel(key),
    startDate,
    endDate,
  });

  const handleRun = () => {
    if (!run) {
      setRun(true);
    } else {
      void query.refetch();
    }
  };

  const handleCsv = (area: ResolvedArea, key: string, variant: CsvVariant = 'full') => {
    const meta = makeMeta(key);
    const suffix = variant === 'parentOnly' ? '_親のみ' : '';
    const base = `${reportFileBase(meta)}${suffix}_${fileTimestamp()}`;
    downloadCsv(`${base}.csv`, toCsv(buildAreaCsvRows(area, meta, variant)));
  };

  const handlePdf = (area: ResolvedArea, key: string) => {
    const meta = makeMeta(key);
    const ok = printAreaReport(area, meta, `${reportFileBase(meta)}_${fileTimestamp()}`);
    if (!ok) window.alert(t('pmoBoard.performanceAgg.popupBlocked'));
  };

  return (
    <Card className="animate-content-reveal">
      <div className="px-6 pt-5 pb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t(`pmoBoard.performanceAgg.blockTitle.${mode}`)}
          </h3>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {t(`pmoBoard.performanceAgg.blockSubtitle.${mode}`)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusIndicator
            isFetching={query.isFetching}
            isDone={run && query.isSuccess && !query.isFetching}
            grandTotal={query.data?.grandTotalPoint ?? 0}
            ticketCount={query.data?.totalTicketCount ?? 0}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleRun}
            disabled={!datesValid || query.isFetching}
          >
            {query.isFetching
              ? t('pmoBoard.performanceAgg.aggregating')
              : run
                ? t('pmoBoard.performanceAgg.reaggregate')
                : t('pmoBoard.performanceAgg.aggregate')}
          </Button>
        </div>
      </div>

      <CardContent>
        {query.isError ? (
          <ErrorState
            title={t('feedback.errorTitle')}
            message={t('feedback.errorMessage')}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : !run ? (
          <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
            {t('pmoBoard.performanceAgg.notRun')}
          </p>
        ) : query.isFetching ? (
          <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
            {t('pmoBoard.performanceAgg.aggregating')}…
          </p>
        ) : areas.length === 0 ? (
          <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
            {t('pmoBoard.performanceAgg.noResult')}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {businessAreas.map((area) => (
                <AreaCard
                  key={area.key}
                  area={area}
                  label={areaLabel(area.key)}
                  onPreview={() => {
                    onPreview({ area, meta: makeMeta(area.key) });
                  }}
                  onCsv={(variant) => {
                    handleCsv(area, area.key, variant);
                  }}
                  onPdf={() => {
                    handlePdf(area, area.key);
                  }}
                />
              ))}
            </div>
            {/* 担当Div 軸のカード（PI Div）は事業カードと同じグリッドに混ぜない。軸が違うので
                合計が足し合わず、並べると「数字が合っていない」と読まれる。破線で区切って
                別ブロックにし、カード内にも集計軸の断りを出す。 */}
            {divisionAreas.length > 0 && (
              <div className="mt-5 border-t border-dashed border-[var(--color-border)] pt-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {divisionAreas.map((area) => (
                    <AreaCard
                      key={area.key}
                      area={area}
                      label={areaLabel(area.key)}
                      hint={t('pmoBoard.performanceAgg.pidivHint')}
                      onPreview={() => {
                        onPreview({ area, meta: makeMeta(area.key) });
                      }}
                      onCsv={(variant) => {
                        handleCsv(area, area.key, variant);
                      }}
                      onPdf={() => {
                        handlePdf(area, area.key);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface AreaCardProps {
  area: ResolvedArea;
  label: string;
  /** 集計軸が他カードと違うときの断り書き（担当Div 軸のカードのみ） */
  hint?: string;
  onPreview: () => void;
  onCsv: (variant?: CsvVariant) => void;
  onPdf: () => void;
}

/**
 * 集計エリア 1 枚分のカード（合計pt・件数 + プレビュー / CSV / CSV(親のみ) / PDF）。
 *
 * 実績が 0 のエリアは出力しても空になるので、4 つのボタンすべてを無効にする。
 * @graph-connects none
 */
function AreaCard({ area, label, hint, onPreview, onCsv, onPdf }: AreaCardProps) {
  const { t } = useTranslation();
  const hasData = area.totalPoint > 0 || area.ticketCount > 0;
  const accent = getAreaAccent(area.key);

  return (
    <div
      className="perf-area-card rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 flex flex-col gap-3"
      style={accent ? ({ '--pt-accent': accent } as CSSProperties) : undefined}
    >
      <div className="flex items-baseline justify-between">
        <span data-card-text className="font-semibold text-[var(--color-text-primary)]">
          {label}
        </span>
        <span data-card-text className="text-sm text-[var(--color-text-muted)]">
          {area.ticketCount}
          {t('pmoBoard.performanceAgg.ticketUnit')}
        </span>
      </div>
      {hint !== undefined && (
        <p data-card-text className="-mt-2 text-[11px] leading-snug text-[var(--color-text-muted)]">
          {hint}
        </p>
      )}
      <div className="pt-number text-2xl font-bold leading-none">
        {formatPoint(area.totalPoint)}
        <span data-card-text className="ml-1 text-sm font-normal text-[var(--color-text-muted)]">
          pt
        </span>
      </div>
      <div className="perf-area-actions mt-auto flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" disabled={!hasData} onClick={onPreview}>
          {t('pmoBoard.performanceAgg.preview')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasData}
          onClick={() => {
            onCsv();
          }}
        >
          CSV
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasData}
          onClick={() => {
            onCsv('parentOnly');
          }}
        >
          {t('pmoBoard.performanceAgg.exportCsvParentShort')}
        </Button>
        <Button variant="ghost" size="sm" disabled={!hasData} onClick={onPdf}>
          PDF
        </Button>
      </div>
    </div>
  );
}

interface StatusIndicatorProps {
  isFetching: boolean;
  isDone: boolean;
  grandTotal: number;
  ticketCount: number;
}

/** 集計中 / 集計完了 のステータス表示。 */
/** @graph-connects none */
function StatusIndicator({ isFetching, isDone, grandTotal, ticketCount }: StatusIndicatorProps) {
  const { t } = useTranslation();
  if (isFetching) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-[var(--color-warning)]">
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        {t('pmoBoard.performanceAgg.aggregating')}
      </span>
    );
  }
  if (isDone) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-[var(--color-success)]">
        <span aria-hidden>✓</span>
        {t('pmoBoard.performanceAgg.aggregated', {
          point: formatPoint(grandTotal),
          count: ticketCount,
        })}
      </span>
    );
  }
  return null;
}
